import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, GOOGLE_MAPS_API_KEY } from './config';
import {
  GoogleMap,
  Marker,
  LoadScript,
  DirectionsRenderer,
} from '@react-google-maps/api';

const Track = () => {
  const { tripId } = useParams();
  const [tripData, setTripData] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directions, setDirections] = useState(null);

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        const response = await fetch(`${API_URL}/trip/${tripId}`);
        const data = await response.json();
        setTripData(Array.isArray(data) ? data[0] : data);
        
        // Fetch driver location if driver_id exists
        if (data && data[0]?.driver_id) {
          const driverResponse = await fetch(`${API_URL}/driver/${data[0].driver_id}`);
          const driverData = await driverResponse.json();
          if (Array.isArray(driverData) && driverData.length > 0) {
            const driver = driverData[0];
            const lat = parseFloat(driver.lat_cordinates);
            const lng = parseFloat(driver.long_cordinates);
            if (!isNaN(lat) && !isNaN(lng)) {
              setDriverLocation({ lat, lng });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching trip data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tripId) {
      fetchTripData();
    }
  }, [tripId, API_URL]);

  useEffect(() => {
    if (tripData && window.google && window.google.maps) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(
            tripData.origin_location_lat,
            tripData.origin_location_long
          ),
          destination: new window.google.maps.LatLng(
            tripData.destination_lat,
            tripData.destination_long
          ),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          }
        }
      );
    }
  }, [tripData]);

  const mapContainerStyle = {
    height: '100vh',
    width: '100%',
  };

  const center = driverLocation
    ? { lat: driverLocation.lat, lng: driverLocation.lng }
    : tripData
    ? { lat: tripData.origin_location_lat, lng: tripData.origin_location_long }
    : { lat: -17.8292, lng: 31.0522 };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #FFC000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading trip tracking...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {tripData && (
            <>
              <Marker
                position={{
                  lat: tripData.origin_location_lat,
                  lng: tripData.origin_location_long,
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#4CAF50',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                }}
                title="Origin"
              />
              <Marker
                position={{
                  lat: tripData.destination_lat,
                  lng: tripData.destination_long,
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#F44336',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                }}
                title="Destination"
              />
              {driverLocation && (
                <Marker
                  position={{
                    lat: driverLocation.lat,
                    lng: driverLocation.lng,
                  }}
                  icon={{
                    path: "M23.5 7c.276 0 .5.224.5.5v.511c0 .793-.926.989-1.616.989l-1.086-2h2.202zm-1.441 3.506c.639 1.186.946 2.252.946 3.666 0 1.37-.397 2.533-1.005 3.981v1.847c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1h-13v1c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1.847c-.608-1.448-1.005-2.611-1.005-3.981 0-1.414.307-2.48.946-3.666.829-1.537 1.851-3.453 2.93-5.252.828-1.382 1.262-1.707 2.278-1.889 1.532-.275 2.918-.365 4.851-.365s3.319.09 4.851.365c1.016.182 1.45.507 2.278 1.889 1.079 1.799 2.101 3.715 2.93 5.252zm-16.059 2.994c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm10 1c0-.276-.224-.5-.5-.5h-7c-.276 0-.5.224-.5.5s.224.5.5.5h7c.276 0 .5-.224.5-.5zm2.941-5.527s-.74-1.826-1.631-3.142c-.202-.298-.515-.502-.869-.566-1.511-.272-2.835-.359-4.441-.359s-2.93.087-4.441.359c-.354.064-.667.268-.869.566-.891 1.315-1.631 3.142-1.631 3.142 1.64.313 4.309.497 6.941.497s5.301-.184 6.941-.497zm2.059 4.527c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm-18.298-6.5h-2.202c-.276 0-.5.224-.5.5v.511c0 .793.926.989 1.616.989l1.086-2z",
                    fillColor: '#FFC000',
                    fillOpacity: 1,
                    strokeWeight: 1,
                    strokeColor: '#000000',
                    scale: 1,
                  }}
                  title="Driver"
                />
              )}
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: '#FFC000',
                      strokeWeight: 4,
                      strokeOpacity: 0.8,
                    },
                  }}
                />
              )}
            </>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default Track;
